import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { getProfileStats, mockCancel, setFavoriteTitle } from "@/lib/api";
import Header from "@/components/Header";
import ProGateModal from "@/components/ProGateModal";
import { colorForName, initialFromName } from "@/lib/avatar";

const CATEGORY_COLORS = {
  vida_cotidiana: "#FFDE59",
  cultura_pop: "#A8FF76",
  comida: "#FF4C4C",
  deportes: "#7DD3FC",
  tecnologia: "#C084FC",
  filosofia: "#FB923C",
  absurdos: "#F9A8D4",
  argentina_profunda: "#6EE7B7",
  conspiranoia: "#FCD34D",
  dilemas_morales: "#F87171",
};

function StatCard({ label, value, sub, delay, testId, color }) {
  return (
    <motion.div
      initial={{ y: 14, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay, duration: 0.35 }}
      className="border-4 border-[#101010] rounded-xl cm-shadow-sm p-4 md:p-5"
      style={{ background: color || "white" }}
      data-testid={testId}
    >
      <div className="font-mono-cm text-[10px] md:text-xs uppercase tracking-widest text-[#101010]/60">
        {label}
      </div>
      <div className="font-display text-3xl md:text-4xl tabular-nums leading-tight mt-1">
        {value}
      </div>
      {sub && (
        <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50 mt-0.5">
          {sub}
        </div>
      )}
    </motion.div>
  );
}

export default function ProfilePage({ user: initialUser }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);
  const [settingTitle, setSettingTitle] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const s = await getProfileStats();
      setStats(s);
    } catch (_e) {
      toast.error("No se pudieron cargar tus estadísticas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onUpgraded = async () => {
    setGateOpen(false);
    setUser((u) => (u ? { ...u, is_pro: true } : u));
    await load();
  };

  const handleSetFavorite = async (titleId) => {
    setSettingTitle(titleId);
    try {
      await setFavoriteTitle(titleId);
      await load();
    } catch (_e) {
      toast.error("No se pudo cambiar el título.");
    } finally {
      setSettingTitle(null);
    }
  };

  const onCancelPro = async () => {
    try {
      await mockCancel();
      toast.success("PRO desactivado.");
      setUser((u) => (u ? { ...u, is_pro: false } : u));
      await load();
    } catch (_e) {
      toast.error("No se pudo cancelar.");
    }
  };

  const color = colorForName(user?.name || user?.email);
  const initial = initialFromName(user?.name || user?.email);

  if (loading || !stats) {
    return (
      <div className="min-h-screen px-4 sm:px-6 py-6 md:py-10">
        <Header user={user} />
        <div className="max-w-4xl mx-auto text-center py-20 font-display uppercase text-2xl" data-testid="profile-loading">
          Cargando perfil...
        </div>
      </div>
    );
  }

  const recentDebates = stats.recent_debates || [];

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 md:py-10">
      <Header user={user} />

      <div className="max-w-4xl mx-auto space-y-6">

        {/* Identity */}
        <motion.div
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="bg-[#FFDE59] border-4 border-[#101010] rounded-xl cm-shadow p-5 md:p-7 flex items-center gap-4 md:gap-6"
        >
          {user?.picture ? (
            <img src={user.picture} alt={user.name} referrerPolicy="no-referrer"
              className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-[#101010]"
              data-testid="profile-avatar" />
          ) : (
            <span className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-[#101010] flex items-center justify-center font-display text-4xl md:text-5xl cm-shadow-sm"
              style={{ background: color }} data-testid="profile-avatar">
              {initial}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-display uppercase text-xl sm:text-2xl md:text-4xl leading-tight truncate" data-testid="profile-name">
              {user?.name}
            </div>
            <div className="font-mono-cm text-[10px] sm:text-xs md:text-sm uppercase tracking-widest text-[#101010]/70 truncate">
              {user?.email}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {user?.is_pro ? (
                <>
                  <span className="font-mono-cm text-[10px] uppercase tracking-widest bg-[#FF4C4C] text-white border border-[#101010] px-2 py-1 rounded" data-testid="profile-pro-badge">
                    PRO Activo
                  </span>
                  <button onClick={onCancelPro} data-testid="profile-cancel-pro-button"
                    className="text-[10px] font-mono-cm uppercase tracking-widest underline underline-offset-4 text-[#101010]/60">
                    Cancelar
                  </button>
                </>
              ) : (
                <button onClick={() => setGateOpen(true)} data-testid="profile-upgrade-button"
                  className="cm-press text-xs font-mono-cm uppercase tracking-widest border-2 border-[#101010] bg-[#FF4C4C] text-white px-3 py-1.5 rounded-md cm-shadow-sm">
                  Desbloquear PRO
                </button>
              )}
              {(stats.play_streak > 0 || stats.win_streak > 0) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {stats.play_streak > 0 && (
                    <span className="font-mono-cm text-[10px] uppercase tracking-widest bg-[#FF4C4C] text-white border border-[#101010] px-2 py-1 rounded">
                      🔥 {stats.play_streak} días de reto
                    </span>
                  )}
                  {stats.win_streak > 0 && (
                    <span className="font-mono-cm text-[10px] uppercase tracking-widest bg-[#FFDE59] text-[#101010] border border-[#101010] px-2 py-1 rounded">
                      🏆 {stats.win_streak} días ganando
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <StatCard label="Partidas" value={stats.games_played} delay={0.05} testId="stat-games-played" />
          <StatCard label="Racha reto" value={stats.play_streak || "—"} sub="días seguidos" delay={0.08} testId="stat-play-streak" color="#FFDE59" />
          <StatCard label="Racha ganadora" value={stats.win_streak || "—"} sub="días con 75+" delay={0.1} testId="stat-win-streak" color="#FF4C4C" />
          <StatCard label="% Victorias" value={`${stats.win_rate}%`} sub={`${stats.games_played > 0 ? Math.round(stats.games_played * stats.win_rate / 100) : 0} ganados`} delay={0.12} testId="stat-win-rate" color="#A8FF76" />
        </div>
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <StatCard label="Mejor puntaje" value={stats.best_score} delay={0.15} testId="stat-best-score" color="#FFDE59" />
          <StatCard label="Promedio" value={stats.avg_score} delay={0.2} testId="stat-avg-score" />
        </div>

        {/* Por categoría */}
        {stats.category_stats?.length > 0 && (
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
          >
            <div className="bg-[#101010] px-5 py-3">
              <span className="font-mono-cm text-[11px] uppercase tracking-widest text-white/70">Rendimiento por categoría</span>
            </div>
            <div className="divide-y-4 divide-[#101010]">
              {stats.category_stats.map((cs) => {
                const bg = CATEGORY_COLORS[cs.category] || "#fff";
                const winPct = cs.played > 0 ? Math.round(cs.wins / cs.played * 100) : 0;
                return (
                  <div key={cs.category} className="flex items-center gap-3 px-4 py-3" style={{ background: bg + "33" }}>
                    <div
                      className="w-3 h-3 rounded-sm border-2 border-[#101010] shrink-0"
                      style={{ background: bg }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase text-sm leading-tight">{cs.label}</div>
                      <div className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50">
                        {cs.played} debate{cs.played !== 1 ? "s" : ""} · {winPct}% victorias
                      </div>
                    </div>
                    {/* Mini bar */}
                    <div className="w-24 shrink-0">
                      <div className="h-2 bg-[#101010]/10 rounded-full overflow-hidden border border-[#101010]/20">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${cs.avg_score}%`, background: cs.avg_score >= 75 ? "#A8FF76" : cs.avg_score >= 65 ? "#FFDE59" : "#FF4C4C" }}
                        />
                      </div>
                      <div className="font-mono-cm text-[10px] text-center tabular-nums text-[#101010]/60 mt-0.5">
                        {cs.avg_score} avg
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Historial de debates */}
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
        >
          <div className="bg-[#101010] px-5 py-3 flex items-center justify-between">
            <span className="font-mono-cm text-[11px] uppercase tracking-widest text-white/70">
              Últimos debates
            </span>
            {!user?.is_pro && (
              <button onClick={() => setGateOpen(true)}
                className="font-mono-cm text-[10px] uppercase tracking-widest text-[#FFDE59] underline underline-offset-2">
                PRO ve los últimos 20 →
              </button>
            )}
          </div>

          {recentDebates.length === 0 ? (
            <div className="p-8 text-center font-mono-cm text-sm uppercase tracking-widest text-[#101010]/60" data-testid="recent-empty">
              Todavía no jugaste ningún debate
            </div>
          ) : (
            <div className="divide-y-4 divide-[#101010]">
              {recentDebates.map((d, i) => {
                const won = (d.total_score || 0) >= 75;
                const scoreColor = won ? "#A8FF76" : (d.total_score || 0) >= 65 ? "#FFDE59" : "#FF4C4C";
                return (
                  <button
                    key={d.debate_id}
                    onClick={() => navigate(`/result/${d.debate_id}`)}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#FFDE59]/20 transition-colors"
                    data-testid={`recent-debate-${i}`}
                  >
                    {/* Score pill */}
                    <div
                      className="w-14 h-14 shrink-0 rounded-xl border-4 border-[#101010] flex flex-col items-center justify-center"
                      style={{ background: scoreColor }}
                    >
                      <div className="font-display text-xl tabular-nums leading-none">{d.total_score}</div>
                      <div className="font-mono-cm text-[8px] uppercase tracking-widest text-[#101010]/60">/100</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate italic">&ldquo;{d.stance}&rdquo;</div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50">
                          {d.category_label}
                        </span>
                        <span className="font-mono-cm text-[10px] text-[#101010]/30">·</span>
                        <span className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50">
                          {new Date(d.created_at).toLocaleDateString("es-AR")}
                        </span>
                        <span
                          className="font-mono-cm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-[#101010]/20"
                          style={{ background: scoreColor + "66" }}
                        >
                          {won ? "Victoria" : (d.total_score || 0) >= 65 ? "Empate" : "Derrota"}
                        </span>
                      </div>
                    </div>
                    <span className="font-mono-cm text-xs text-[#101010]/30 shrink-0">→</span>
                  </button>
                );
              })}
            </div>
          )}

          {!user?.is_pro && recentDebates.length >= 5 && (
            <div className="border-t-4 border-[#101010] p-4 bg-[#F0EDE1] text-center">
              <button onClick={() => setGateOpen(true)}
                className="cm-press font-mono-cm text-[11px] uppercase tracking-widest underline underline-offset-2 text-[#101010]/60">
                🔒 Desbloqueá PRO para ver tu historial completo
              </button>
            </div>
          )}
        </motion.div>

        {/* Logros */}
        {stats.titles?.length > 0 && (
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden"
          >
            <div className="bg-[#101010] px-5 py-3 flex items-center gap-2">
              <span className="font-mono-cm text-[11px] uppercase tracking-widest text-white/70">Logros</span>
              <span className="font-mono-cm text-[10px] uppercase tracking-widest bg-[#FFDE59] text-[#101010] px-1.5 py-0.5 rounded">
                {stats.titles.filter((t) => t.unlocked).length}/{stats.titles.length}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y-4 sm:divide-y-0 sm:divide-x-0 divide-[#101010]">
              {stats.titles.map((title, i) => {
                const isActive = title.is_favorite;
                return (
                  <div
                    key={title.id}
                    className={`flex items-start gap-3 p-4 border-b-4 border-[#101010] last:border-b-0 sm:last:border-b-4 transition-colors ${
                      isActive ? "bg-[#FFDE59]" : title.unlocked ? "bg-white" : "bg-[#F0EDE1]"
                    }`}
                    style={{ opacity: title.unlocked ? 1 : 0.5 }}
                    data-testid={`title-card-${title.id}`}
                  >
                    <div className="text-2xl shrink-0 leading-none mt-0.5">
                      {title.unlocked ? title.icon : "🔒"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-display uppercase text-sm leading-tight">
                          {title.label}
                        </span>
                        {isActive && (
                          <span className="font-mono-cm text-[8px] uppercase tracking-widest bg-[#101010] text-[#FFDE59] px-1.5 py-0.5 rounded shrink-0">
                            Activo
                          </span>
                        )}
                      </div>
                      <p className="font-mono-cm text-[10px] uppercase tracking-widest text-[#101010]/50 leading-tight mt-0.5">
                        {title.description}
                      </p>
                      {title.unlocked && title.unlocked_at && (
                        <p className="font-mono-cm text-[10px] text-[#101010]/40 mt-0.5">
                          Desbloqueado {new Date(title.unlocked_at).toLocaleDateString("es-AR")}
                        </p>
                      )}
                    </div>
                    {title.unlocked && (
                      <button
                        onClick={() => !isActive && handleSetFavorite(title.id)}
                        disabled={isActive || settingTitle === title.id}
                        className={`shrink-0 font-mono-cm text-[9px] uppercase tracking-widest border-2 border-[#101010] px-2 py-1 rounded transition-colors ${
                          isActive
                            ? "bg-[#101010] text-[#FFDE59] cursor-default"
                            : "bg-white hover:bg-[#FFDE59] cursor-pointer cm-press"
                        } disabled:opacity-60`}
                        data-testid={`title-use-${title.id}`}
                      >
                        {settingTitle === title.id ? "..." : isActive ? "✓ En uso" : "Usar"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

      </div>

      <ProGateModal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        onUpgraded={onUpgraded}
        reason="history"
      />
    </div>
  );
}
